import type { AdType } from '@/modules/adv/types'
import type { GameAttributes, GameEvent, User } from '@/types'
import type { ApiAcknowledgement, ApiResult } from '@/utils/requests/api-result'
import type { FetchResult } from '@/utils/requests/types'

import { MessageAction, type OutcomingMessage } from '../main-frame/message-types'
import { normalizePurchaseCurrency } from './money'
import type { CatalogProduct, OrderPricing, PricedAmount } from './pricing'

/** `amount` is in `currency` (fiat in minor units); `gamAmount` is what the wallet side works with. */
export interface Order extends PricedAmount {
    gameHru: string
    gameTitle: string
    type: 'order'
    attributes: GameAttributes
    description?: string
    subtitle?: string
    photoUrl?: string
    externalId?: string
    bridgeId?: string
    placement?: AdType
}

export type DisableAdOrder = {
    amount: number
    type: 'disable-ad-order'
    durationInDays: 1 | 7 | 30
    externalId: string
    attributes: GameAttributes
    placement?: AdType
    isTopUp?: boolean
    subscription: 'basic-days'
}

export interface Topup {
    amount?: number
    type: 'topup'
}

interface Purchase {
    id: string
    purchaseToken: string
    status: string
    externalId: string
    bridgeId?: string
    [key: string]: unknown
}

export type PurchaseStatus = PaidStatus | FailedStatus

interface PaidStatus {
    status: 'PAID'
    orderId: string
    amount: number
    externalId?: string
}

interface FailedStatus {
    status: 'FAILED'
    error: string
}

export type ChargeResponse<T extends Order | DisableAdOrder | Topup> = T extends Order | DisableAdOrder
    ? Awaited<FetchResult<PurchaseStatus>>
    : User | null

export type PaymentModalOrderResponse<T = Order | DisableAdOrder | Topup | null> = T extends
    | Order
    | DisableAdOrder
    | Topup
    ? Promise<ChargeResponse<T>>
    : undefined

export type ChargeUserFunction = <T extends Order | DisableAdOrder | Topup | null>(
    value: T,
) => PaymentModalOrderResponse<T>

interface IgpParams {
    gameId: string
    orderId: string
    externalId: string
}

interface CreateInGamePaymentsOptions {
    gameId: string
    gameHru: string
    gameTitle: string
    chargeUser: ChargeUserFunction
    pricing: OrderPricing
    attributes: GameAttributes
    /** Identifies the live game session; a purchase quoted in one session must not open the modal in another. */
    getSessionToken?: () => unknown
    confirmDelivery?: (params: IgpParams) => Promise<ApiAcknowledgement>
    getPurchases?: (gameId: string) => Promise<ApiResult<Purchase[]>>
    consumePurchase?: (params: IgpParams) => Promise<ApiAcknowledgement>
}

const purchaseMessage = (res: Awaited<FetchResult<PurchaseStatus>>, event: GameEvent) => {
    let payload = res.data
    if (res.errors.length) {
        payload = {
            status: 'FAILED',
            error: res.errors.join(', '),
        }
    }

    return {
        type: 'in-game-payments',
        action: MessageAction.purchaseStatus,
        originalMessageEvent: event,
        payload,
    } as OutcomingMessage
}

// A game that priced in Gam gets its receipt in Gam, whatever currency the portal charged in
const toGameUnits = (res: Awaited<FetchResult<PurchaseStatus>>, gamAmount: number) => {
    if (res.data?.status !== 'PAID') {
        return res
    }

    const { currency, ...paid } = res.data as PurchaseStatus & { currency?: string }
    return { ...res, data: { ...paid, amount: gamAmount } }
}

export const createInGamePayments = ({
    gameId,
    gameHru,
    gameTitle,
    chargeUser,
    pricing,
    attributes,
    getSessionToken,
    confirmDelivery,
    getPurchases,
    consumePurchase,
}: CreateInGamePaymentsOptions) => {
    const messageHandler = async (event: GameEvent) => {
        switch (event.data.action) {
            case MessageAction.requestPurchase: {
                const session = getSessionToken?.()
                // Once the iframe has been replaced the requesting game is gone, and the platform SDK
                // in the new iframe settles whatever purchase it has pending with any answer it gets,
                // so a stale operation must neither charge nor answer
                const isStale = () => getSessionToken !== undefined && getSessionToken() !== session
                try {
                    const { amount, currency, ...product } = event.data.payload ?? {}
                    const priced = await pricing.priceAmount({ amount, currency })
                    if (isStale()) {
                        return undefined
                    }

                    const purchaseData = await chargeUser<Order>({
                        type: 'order',
                        gameHru,
                        gameTitle,
                        attributes,
                        ...product,
                        ...priced,
                    } as Order)
                    // Absent (undefined or null) currency means the game priced in Gam
                    return purchaseMessage(
                        normalizePurchaseCurrency(currency) === undefined
                            ? toGameUnits(purchaseData, priced.gamAmount)
                            : purchaseData,
                        event,
                    )
                } catch (e) {
                    console.error('Caught error during purchase request:', e)
                    if (isStale()) {
                        return undefined
                    }

                    return purchaseMessage(
                        {
                            errors: [e?.toString() as string],
                            data: null,
                            // @ts-expect-error fill later
                            originalResponse: null,
                        },
                        event,
                    )
                }
            }
            case MessageAction.requestTopup: {
                try {
                    const userInfo = await chargeUser({ type: 'topup', ...event.data.payload } as Topup)
                    return {
                        type: 'in-game-payments',
                        action: MessageAction.topupStatus,
                        originalMessageEvent: event as GameEvent,
                        payload: userInfo,
                    } as OutcomingMessage
                } catch (e) {
                    return purchaseMessage(
                        {
                            errors: [e?.toString() as string],
                            data: null,
                            // @ts-expect-error fill later
                            originalResponse: null,
                        },
                        event,
                    )
                }
            }
            case MessageAction.confirmDelivery: {
                if (confirmDelivery) {
                    const { orderId, externalId } = (event.data.payload ?? {}) as {
                        orderId: string
                        externalId: string
                    }
                    const result = await confirmDelivery({ gameId, orderId, externalId })

                    if (result.status === 'error') {
                        console.error('Caught error during confirmDelivery:', result.message)
                    }
                }

                return {
                    type: 'in-game-payments',
                    action: MessageAction.confirmDeliveryStatus,
                    originalMessageEvent: event,
                    payload: {},
                } as OutcomingMessage
            }
            case MessageAction.getCatalog: {
                // An empty list makes the Bridge fall back to its Gam mapping, so a quote failure
                // degrades the display only; the purchase itself is priced again on request.
                let products: CatalogProduct[] = []
                try {
                    products = await pricing.getCatalog(event.data.payload?.products)
                } catch (e) {
                    console.error('Caught error during getCatalog:', e)
                }

                return {
                    type: 'in-game-payments',
                    action: MessageAction.catalogResponse,
                    originalMessageEvent: event,
                    payload: { products },
                } as OutcomingMessage
            }
            case MessageAction.getPurchases: {
                const result = getPurchases ? await getPurchases(gameId) : { status: 'ok' as const, data: [] }
                if (result.status === 'error') {
                    console.error('Caught error during getPurchases:', result.message)
                }

                return {
                    type: 'in-game-payments',
                    action: MessageAction.purchasesResponse,
                    originalMessageEvent: event,
                    payload: { purchases: result.status === 'ok' ? result.data : [] },
                } as OutcomingMessage
            }
            case MessageAction.consumePurchase: {
                if (consumePurchase) {
                    const { orderId, externalId } = (event.data.payload ?? {}) as {
                        orderId: string
                        externalId: string
                    }

                    const result = await consumePurchase({ gameId, orderId, externalId })
                    if (result.status === 'error') {
                        console.error('Caught error during consumePurchase:', result.message)
                        return {
                            action: MessageAction.consumePurchaseStatus,
                            originalMessageEvent: event,
                            payload: { error: result.message },
                            type: 'in-game-payments',
                        } as OutcomingMessage
                    }
                }

                return {
                    type: 'in-game-payments',
                    action: MessageAction.consumePurchaseStatus,
                    originalMessageEvent: event,
                    payload: {},
                } as OutcomingMessage
            }
            default:
                return {
                    action: MessageAction.unknownMessageType,
                    originalMessageEvent: event as GameEvent,
                    payload: {
                        error: `Unknown message action "${event.data.action}"`,
                    },
                    type: 'error',
                } as OutcomingMessage
        }
    }

    return messageHandler
}

/** Resolves to no message when the operation belongs to a game session that has ended. */
export type InGamePaymentsMessageHandler = (event: GameEvent) => Promise<OutcomingMessage | undefined>
